import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy, getDoc, setDoc
} from 'firebase/firestore';
import AdminLayout from './AdminLayout';
import ImageUploader from '../../components/ImageUploader';
import { Plus, Edit2, Trash2, X, Save, Calendar, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const YOUNG_LEVELS_OPTIONS = ['A1', 'A2/B1', 'B2/C1', 'A2 – B1', 'B2 – C1', 'A2', 'B1', 'B2', 'C1'];
const ADULT_LEVELS_OPTIONS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const EMPTY_FORM = { date: '', deadline: '', examDate: '', learnerType: 'both', levels: [], year: new Date().getFullYear(), description: '', link: '' };
const LEARNER_TYPE_OPTIONS = [
  { value: 'adult', label: 'General (18+)' },
  { value: 'young', label: 'Young Learner (11–17)' },
  { value: 'both', label: 'Both' },
];

function AutoStatusBadge({ deadline }) {
  const isOpen = new Date() < new Date(deadline);
  return isOpen ? (
    <span className="badge-active flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Open</span>
  ) : (
    <span className="badge-new flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Closed</span>
  );
}

export default function ExamTracker() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [customLevel, setCustomLevel] = useState('');

  const [calendarImg, setCalendarImg] = useState('');

  const fetchSettings = async () => {
    if (!db) return;
    try {
      const snap = await getDoc(doc(db, 'settings', 'exams'));
      if (snap.exists()) setCalendarImg(snap.data().calendarImageURL || '');
    } catch {}
  };

  const fetchExams = async () => {
    setLoading(true);
    if (!db) { setLoading(false); return; }
    try {
      const snap = await getDocs(collection(db, 'exams'));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      // Sort by examDate ascending (fallback to deadline)
      data.sort((a, b) => {
        const dateA = a.examDate ? new Date(a.examDate).getTime() : (a.deadline ? new Date(a.deadline).getTime() : 0);
        const dateB = b.examDate ? new Date(b.examDate).getTime() : (b.deadline ? new Date(b.deadline).getTime() : 0);
        return dateA - dateB;
      });
      
      setExams(data);
    } catch { toast.error('Failed to load exams'); }
    setLoading(false);
  };

  useEffect(() => { fetchExams(); fetchSettings(); }, []);

  const handleSettingsSave = async (url) => {
    try {
      console.log('[ExamTracker] Saving calendar image URL to Firestore:', url);
      await setDoc(doc(db, 'settings', 'exams'), { calendarImageURL: url }, { merge: true });
      setCalendarImg(url);
      toast.success('Calendar image updated!');
    } catch (err) {
      console.error('[ExamTracker] Firestore save error:', err?.code, err?.message, err);
      toast.error('Failed to update calendar image.');
    }
  };

  const openAdd = () => { setForm(EMPTY_FORM); setEditId(null); setShowModal(true); };
  const openEdit = (exam) => { setForm({ ...exam }); setEditId(exam.id); setShowModal(true); };

  const toggleLevel = (l) => {
    setForm((p) => ({
      ...p,
      levels: p.levels?.includes(l) ? p.levels.filter((x) => x !== l) : [...(p.levels || []), l],
    }));
  };

  const addCustomLevel = () => {
    const val = customLevel.trim();
    if (!val) return;
    if (!form.levels?.includes(val)) {
      setForm((p) => ({
        ...p,
        levels: [...(p.levels || []), val],
      }));
    }
    setCustomLevel('');
  };

  const handleSave = async () => {
    if (!form.date?.trim()) { toast.error('Session name is required (e.g. "May 2025").'); return; }
    if (!form.deadline) { toast.error('Registration deadline is required.'); return; }
    if (!db) { toast.error('Firebase is not configured. Cannot save in demo mode.'); return; }
    setSaving(true);
    try {
      const data = { ...form, date: form.date.trim(), year: Number(form.year), updatedAt: serverTimestamp() };
      if (editId) {
        await updateDoc(doc(db, 'exams', editId), data);
        toast.success('Exam updated!');
      } else {
        await addDoc(collection(db, 'exams'), { ...data, createdAt: serverTimestamp() });
        toast.success('Exam session added!');
      }
      setShowModal(false);
      fetchExams();
    } catch (err) { console.error(err); toast.error('Failed to save: ' + (err?.message || 'Unknown error')); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this exam session?')) return;
    await deleteDoc(doc(db, 'exams', id));
    toast.success('Deleted');
    fetchExams();
  };

  return (
    <AdminLayout>
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-extrabold text-2xl text-onSurface">Exam Tracker</h1>
            <p className="text-sm text-onSurfaceVariant mt-1">Manage DELE exam sessions and registration windows</p>
          </div>
          <button onClick={openAdd} className="btn-primary text-sm">
            <Plus className="w-4 h-4" /> Add Session
          </button>
        </div>

        {/* Global Settings */}
        <div className="bg-white rounded-2xl shadow-card p-6 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex-1">
             <h2 className="font-display font-bold text-lg text-onSurface mb-2">Calendar Image</h2>
             <p className="text-sm text-onSurfaceVariant mb-4 max-w-md">Upload an image to display above the exam session cards on the public page.</p>
             <ImageUploader label="Upload Image" folder="settings" onUploadComplete={handleSettingsSave} />
          </div>
          {calendarImg && (
             <div className="w-full md:w-64 h-32 rounded-xl border border-surface-high overflow-hidden bg-surface-high flex-shrink-0">
               <img src={calendarImg} alt="Global Calendar" className="w-full h-full object-cover" />
             </div>
          )}
        </div>

        <div className="card-static overflow-hidden">
          {loading ? (
            <div className="p-8 space-y-4">
              {[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-surface-high rounded-xl animate-pulse" />)}
            </div>
          ) : exams.length === 0 ? (
            <div className="p-16 text-center">
              <Calendar className="w-12 h-12 text-surface-dim mx-auto mb-4" />
              <p className="text-onSurfaceVariant">No exam sessions added yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="bg-surface-low border-b border-surface-high">
                    {['Session', 'Exam Date', 'Reg. Deadline', 'Learner Type', 'Levels', 'Status', 'Actions'].map((h) => (
                      <th key={h} className="text-left px-6 py-3 text-xs font-semibold text-onSurfaceVariant uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-high">
                  {exams.map((exam) => (
                    <tr key={exam.id} className="hover:bg-surface-low transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-medium text-sm text-onSurface">{exam.date}</p>
                        <p className="text-xs text-onSurfaceVariant">{exam.description}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-onSurface">
                        {exam.examDate ? new Date(exam.examDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-onSurface">
                        {exam.deadline ? new Date(exam.deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-6 py-4">
                        {exam.learnerType === 'young' && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-lg">Young Learner</span>
                        )}
                        {exam.learnerType === 'adult' && (
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-lg">General</span>
                        )}
                        {(!exam.learnerType || exam.learnerType === 'both') && (
                          <span className="px-2 py-1 bg-surface-high text-onSurfaceVariant text-xs font-bold rounded-lg">Both</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {exam.levels?.map((l) => (
                            <span key={l} className="px-2 py-0.5 bg-primary-light/30 text-primary-dark text-xs font-bold rounded">{l}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <AutoStatusBadge deadline={exam.deadline} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button onClick={() => openEdit(exam)} className="w-8 h-8 rounded-lg hover:bg-surface-low flex items-center justify-center transition-colors">
                            <Edit2 className="w-4 h-4 text-onSurfaceVariant" />
                          </button>
                          <button onClick={() => handleDelete(exam.id)} className="w-8 h-8 rounded-lg hover:bg-primary-light/20 flex items-center justify-center transition-colors">
                            <Trash2 className="w-4 h-4 text-primary-container" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-onSurface/40 backdrop-blur-sm">
          <div className="bg-surface-lowest rounded-2xl shadow-float w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-lowest flex items-center justify-between px-8 py-5 border-b border-surface-high">
              <h2 className="font-display font-bold text-lg">{editId ? 'Edit Exam Session' : 'New Exam Session'}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg hover:bg-surface-low flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-8 space-y-5">
              <div>
                <label className="label-sm text-xs text-onSurfaceVariant mb-1 block">Session Name * (e.g. "May 2025")</label>
                <input type="text" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} className="input-field" placeholder="May 2025" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-sm text-xs text-onSurfaceVariant mb-1 block">Registration Deadline *</label>
                  <input type="date" value={form.deadline} onChange={(e) => setForm((p) => ({ ...p, deadline: e.target.value }))} className="input-field" />
                </div>
                <div>
                  <label className="label-sm text-xs text-onSurfaceVariant mb-1 block">Examination Date</label>
                  <input type="date" value={form.examDate || ''} onChange={(e) => setForm((p) => ({ ...p, examDate: e.target.value }))} className="input-field" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-sm text-xs text-onSurfaceVariant mb-1 block">Learner Type</label>
                  <div className="flex flex-col gap-2 mt-1">
                    {LEARNER_TYPE_OPTIONS.map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="learnerType"
                          value={opt.value}
                          checked={(form.learnerType || 'both') === opt.value}
                          onChange={(e) => setForm((p) => ({ ...p, learnerType: e.target.value }))}
                          className="accent-primary-container"
                        />
                        <span className="text-sm text-onSurface">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="label-sm text-xs text-onSurfaceVariant mb-1 block">Year</label>
                  <input type="number" value={form.year} onChange={(e) => setForm((p) => ({ ...p, year: e.target.value }))} className="input-field" />
                </div>
              </div>
              <div>
                <label className="label-sm text-xs text-onSurfaceVariant mb-2 block">Selected Levels</label>
                {form.levels && form.levels.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {form.levels.map((l) => (
                      <span key={l} className="flex items-center gap-1.5 px-3 py-1 bg-primary-container text-white text-xs font-bold rounded-lg shadow-sm">
                        {l}
                        <button
                          type="button"
                          onClick={() => toggleLevel(l)}
                          className="w-4 h-4 rounded-full bg-white/25 hover:bg-white/40 flex items-center justify-center text-[10px] font-extrabold transition-colors"
                          title="Remove level"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-onSurfaceVariant italic mb-4">No levels selected yet. Select from presets below or add a custom level.</p>
                )}

                <label className="label-sm text-xs text-onSurfaceVariant mb-2 block">Preset Level Options</label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(form.learnerType === 'young' ? YOUNG_LEVELS_OPTIONS : ADULT_LEVELS_OPTIONS).map((l) => {
                    const isSelected = form.levels?.includes(l);
                    return (
                      <button
                        key={l}
                        type="button"
                        onClick={() => toggleLevel(l)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                          isSelected
                            ? 'bg-primary-container text-white border-primary-container'
                            : 'border-surface-high text-onSurfaceVariant hover:border-primary-container bg-white'
                        }`}
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>

                <label className="label-sm text-xs text-onSurfaceVariant mb-1.5 block">Add Custom Level (with separators or single)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customLevel}
                    onChange={(e) => setCustomLevel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomLevel();
                      }
                    }}
                    placeholder="e.g. A2 – B1 or B2/C1"
                    className="input-field py-2 text-xs"
                  />
                  <button
                    type="button"
                    onClick={addCustomLevel}
                    className="px-4 py-2 bg-surface-low hover:bg-primary-light hover:text-primary-dark font-bold text-xs rounded-xl border border-surface-high transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
              <div>
                <label className="label-sm text-xs text-onSurfaceVariant mb-1 block">Registration Link (External URL)</label>
                <input type="url" value={form.link || ''} onChange={(e) => setForm((p) => ({ ...p, link: e.target.value }))} className="input-field" placeholder="https://forms.google.com/..." />
                <p className="text-[10px] text-onSurfaceVariant mt-1">If provided, the website will redirect students directly to this external form instead of standard inquiry.</p>
              </div>
              <div>
                <label className="label-sm text-xs text-onSurfaceVariant mb-1 block">Description</label>
                <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={3} className="input-field resize-none" placeholder="Session notes…" />
              </div>

              {/* Live status preview */}
              {form.deadline && (
                <div className="flex items-center gap-3 p-3 bg-surface-low rounded-xl">
                  <Calendar className="w-4 h-4 text-onSurfaceVariant" />
                  <span className="text-sm text-onSurfaceVariant">Auto-status will show as:</span>
                  <AutoStatusBadge deadline={form.deadline} />
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-surface-lowest px-8 py-5 border-t border-surface-high flex gap-3">
              <button onClick={() => setShowModal(false)} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 justify-center disabled:opacity-60">
                {saving ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Saving…</span> : <><Save className="w-4 h-4" /> Save Session</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
